import React, { useState, useEffect } from 'react';
import './FeatureSlider.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CCTVImg from '../../Media/Images/Slide_CCTV.jpeg';
import PostFactoImg from '../../Media/Images/post-facto-feature-img.jpeg';
import Sensitive from '../../Media/Images/sensitive.jpg';
import People from '../../Media/Images/people.jpg';
import LadyWithWatch from '../../Media/Images/lady-with-watch-croam-img.jpg';
import RecordReport from '../../Media/Images/record-and-report-img.jpg';
import LocationAware from '../../Media/Images/locationAware.png';
import Riskometer from '../../Media/Images/riskometer_new_img.jpg';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../UserContext';

const FeatureSlider = () => {
	const navigate = useNavigate();
	const { user } = useUser();
	const [activeSlides, setActiveSlides] = useState<{ [key: number]: number }>({
		1: 0,
		2: 0,
		3: 0,
		4: 0,
	});

	const features = [
		{
			id: 1,
			title: 'No more Post-Facto',
			description: 'Eliminating after-the-fact corrections ensuring timely and accurate Reporting.',
			images: [PostFactoImg, RecordReport],
			gradient: 'from-blue-600 to-purple-600',
			bgColor: 'bg-blue-50',
			accentColor: 'text-blue-600',
		},
		{
			id: 2,
			title: 'Express Reporting - Any Time, Any Where',
			description: 'Report using Mobile and smart-wearables such as Smartwatches. Quick and easy one-click activation.',
			images: [LadyWithWatch, CCTVImg],
			gradient: 'from-green-600 to-teal-600',
			bgColor: 'bg-green-50',
			accentColor: 'text-green-600',
		},
		{
			id: 3,
			title: 'Location Aware, Scene Aware',
			description:
				'Actual location based Reporting System. Analyzes and evaluates the potential risk of the situation.',
			images: [LocationAware, Riskometer],
			gradient: 'from-orange-600 to-red-600',
			bgColor: 'bg-orange-50',
			accentColor: 'text-orange-600',
		},
		{
			id: 4,
			title: 'People Centric',
			description:
				'Enhances Safety and Security by facilitating direct community engagement. Experience news that puts the community first.',
			images: [Sensitive, People],
			gradient: 'from-purple-600 to-pink-600',
			bgColor: 'bg-purple-50',
			accentColor: 'text-purple-600',
		},
	];

	// Auto-slide functionality
	useEffect(() => {
		const intervals = features.map((feature) => {
			return setInterval(
				() => {
					setActiveSlides((prev) => ({
						...prev,
						[feature.id]: (prev[feature.id] + 1) % feature.images.length,
					}));
				},
				4000 + feature.id * 500
			); // Stagger the intervals
		});

		return () => intervals.forEach(clearInterval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const nextSlide = (featureId: number, imagesLength: number) => {
		setActiveSlides((prev) => ({
			...prev,
			[featureId]: (prev[featureId] + 1) % imagesLength,
		}));
	};

	const prevSlide = (featureId: number, imagesLength: number) => {
		setActiveSlides((prev) => ({
			...prev,
			[featureId]: prev[featureId] === 0 ? imagesLength - 1 : prev[featureId] - 1,
		}));
	};

	return (
		<div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
			{/* Section Header */}
			<div className='text-center mb-16'>
				<h2 className='text-4xl lg:text-5xl font-bold text-[#022c56] mb-4'>Revolutionary Features</h2>
				<div className='w-24 h-1 bg-[#022c56] mx-auto mb-8'></div>
				<p className='text-lg text-gray-600 max-w-3xl mx-auto'>
					Advanced technology features that make B-Reporter the future of authentic reporting
				</p>
			</div>

			{/* Features Column Layout */}
			<div className='space-y-16'>
				{features.map((feature, index) => (
					<div
						key={feature.id}
						className={`${feature.bgColor} rounded-3xl p-8 lg:p-12 shadow-xl hover:shadow-2xl transition-all duration-500`}
					>
						<div
							className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-12`}
						>
							{/* Content Section */}
							<div className='flex-1 space-y-3'>
								<div className='flex items-center gap-4'>
									<div
										className={`w-12 h-12 flex-shrink-0 rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center shadow-lg`}
									>
										<span className='text-white font-bold text-xl'>{feature.id}</span>
									</div>
									<h3 className={`text-3xl lg:text-4xl font-bold ${feature.accentColor}`}>{feature.title}</h3>
								</div>

								<p className='text-lg text-gray-700 leading-relaxed'>{feature.description}</p>

								{/* Feature Highlights */}
								<div className='flex flex-wrap gap-3 justify-center'>
									{feature.id === 1 && (
										<>
											<span className='px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium'>
												Real-time
											</span>
											<span className='px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium'>
												Accurate
											</span>
										</>
									)}
									{feature.id === 2 && (
										<>
											<span className='px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium'>
												Mobile
											</span>
											<span className='px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium'>
												Smartwatch
											</span>
										</>
									)}
									{feature.id === 3 && (
										<>
											<span className='px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium'>
												GPS
											</span>
											<span className='px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium'>
												AI Analysis
											</span>
										</>
									)}
									{feature.id === 4 && (
										<>
											<span className='px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium'>
												Community
											</span>
											<span className='px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium'>
												Safety
											</span>
										</>
									)}
								</div>
							</div>

							{/* Image Slider Section */}
							<div className='flex-1 w-full max-w-md lg:max-w-lg'>
								<div className='relative group'>
									{/* Main Image Container */}
									<div className='relative h-80 lg:h-96 rounded-2xl overflow-hidden shadow-2xl'>
										<div className='relative w-full h-full'>
											{feature.images.map((image, imgIndex) => (
												<div
													key={imgIndex}
													className={`absolute inset-0 transition-all duration-700 ease-in-out ${
														activeSlides[feature.id] === imgIndex ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
													}`}
												>
													<img
														src={image}
														alt={`${feature.title} ${imgIndex + 1}`}
														className='w-full h-full object-cover'
													/>
													<div className={`absolute inset-0 bg-gradient-to-t from-black/30 to-transparent`}></div>
												</div>
											))}
										</div>

										{/* Navigation Buttons */}
										<button
											onClick={() => prevSlide(feature.id, feature.images.length)}
											className='absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100'
										>
											<ChevronLeft className='w-6 h-6 text-white' />
										</button>

										<button
											onClick={() => nextSlide(feature.id, feature.images.length)}
											className='absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100'
										>
											<ChevronRight className='w-6 h-6 text-white' />
										</button>

										{/* Slide Indicators */}
										<div className='absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2'>
											{feature.images.map((_, imgIndex) => (
												<button
													key={imgIndex}
													onClick={() => setActiveSlides((prev) => ({ ...prev, [feature.id]: imgIndex }))}
													className={`w-3 h-3 rounded-full transition-all duration-300 ${
														activeSlides[feature.id] === imgIndex
															? 'bg-white scale-125'
															: 'bg-white/50 hover:bg-white/70'
													}`}
												/>
											))}
										</div>
									</div>

									{/* Image Counter */}
								</div>
							</div>
						</div>
					</div>
				))}
			</div>

			{/* Bottom CTA Section */}
			<div className='text-center mt-16 p-8 lg:p-12 bg-gradient-to-r from-[#022c56] via-[#034068] to-[#045580] rounded-3xl shadow-2xl'>
				<h3 className='text-3xl lg:text-4xl font-bold text-white mb-6'>Ready to Experience the Future of Reporting?</h3>
				<p className='text-xl text-blue-200 mb-8 max-w-3xl mx-auto leading-relaxed'>
					Join thousands of users who are already making a difference with authentic, location-aware reporting
				</p>
				<button
					onClick={() => navigate(user ? '/news' : '/login')}
					className='px-10 custom-get-started bg-yellow-500 hover:bg-yellow-400 text-[#022c56] font-bold text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
				>
					Get Started Today
				</button>
			</div>
		</div>
	);
};

export default FeatureSlider;
